import React, { useMemo } from 'react';
import { Lock, X, Gem } from 'lucide-react';
import { MAP_LABEL, STAGE_ICON } from './gbApi';
import { gbCost } from './GbNodeSheet';
import { GbTank, GbCrest } from './GbTank';
import { GbFit } from './GbFit';

// 4 pontos por cidade — o centro do ícone da fase fica exatamente nestas coordenadas
const SPOTS = [
  [{ x: 17, y: 56 }, { x: 38, y: 38 }, { x: 62, y: 54 }, { x: 84, y: 36 }],
  [{ x: 16, y: 38 }, { x: 39, y: 56 }, { x: 63, y: 36 }, { x: 85, y: 54 }],
  [{ x: 18, y: 54 }, { x: 40, y: 36 }, { x: 61, y: 56 }, { x: 84, y: 40 }],
  [{ x: 17, y: 36 }, { x: 37, y: 54 }, { x: 63, y: 40 }, { x: 85, y: 56 }],
  [{ x: 15, y: 54 }, { x: 38, y: 40 }, { x: 62, y: 56 }, { x: 85, y: 38 }],
  [{ x: 18, y: 40 }, { x: 41, y: 56 }, { x: 64, y: 36 }, { x: 86, y: 52 }],
];

const FLAG = { atual: '#ff3b47', parcial: '#ffc02e', ok: '#2fd47a' };

const Flag = ({ tone }) => (
  <svg viewBox="0 0 34 40" className="h-9 w-8 drop-shadow-[0_3px_6px_rgba(0,0,0,.7)]">
    <rect x="6" y="3" width="3" height="35" rx="1.5" fill="#d8cfe8" />
    <circle cx="7.5" cy="3" r="2.4" fill="#f4eefc" />
    <path d="M9 5 C 20 6, 24 11, 31 10 C 25 14, 22 18, 9 19 Z" fill={tone}>
      <animate attributeName="d" dur="2.4s" repeatCount="indefinite"
        values="M9 5 C 20 6, 24 11, 31 10 C 25 14, 22 18, 9 19 Z;
                M9 5 C 18 9, 26 8, 31 12 C 24 13, 21 19, 9 19 Z;
                M9 5 C 20 6, 24 11, 31 10 C 25 14, 22 18, 9 19 Z" />
    </path>
  </svg>
);

const Spot = ({ n, pos, estado, estrelas, atualNode, onPick }) => {
  const tone = estado === 'ok' ? FLAG.ok : estado === 'parcial' ? FLAG.parcial : FLAG.atual;
  const locked = estado === 'locked';
  const custo = gbCost(n.cost, estrelas);
  const anel = locked ? 'border-purple-900/80' : estado === 'ok' ? 'border-emerald-400/90'
    : estado === 'parcial' ? 'border-amber-400/90' : 'border-rose-400/90';
  return (
    <button type="button" disabled={locked} onClick={() => onPick(n)}
      data-testid={`gb-node-${n.node}`} data-flag={locked ? 'none' : estado}
      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
      className={`absolute z-10 h-16 w-16 -translate-x-1/2 -translate-y-1/2 transition-transform sm:h-[76px] sm:w-[76px]
        ${locked ? 'cursor-not-allowed' : 'hover:scale-110 active:scale-95'}`}>
      {!locked && (
        <span className="absolute -top-7 left-1/2 -translate-x-1/2">
          <Flag tone={tone} />
        </span>
      )}
      {estado === 'atual' && n.node === atualNode && (
        <span className="absolute inset-0 -m-1 animate-ping rounded-full bg-rose-500/25" />
      )}
      <span className={`absolute inset-0 flex items-center justify-center rounded-full border-2 ${anel}
        bg-[radial-gradient(circle_at_50%_35%,rgba(58,34,92,.92),rgba(12,6,22,.94))]
        shadow-[0_6px_16px_rgba(0,0,0,.6)]`}>
        <img src={STAGE_ICON(n.node)} alt="" draggable="false"
          className={`h-[78%] w-[78%] object-contain ${locked ? 'brightness-[.35] saturate-0' : 'drop-shadow-[0_2px_5px_rgba(0,0,0,.6)]'}`} />
        {locked && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Lock className="h-4 w-4 text-purple-200/80" />
          </span>
        )}
      </span>
      <span className={`absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 text-[11px] font-black
        ${locked ? 'border-purple-800 bg-[#150c26] text-purple-400'
          : estado === 'ok' ? 'border-emerald-300 bg-emerald-700 text-white'
            : estado === 'parcial' ? 'border-amber-300 bg-amber-600 text-[#2a1600]'
              : 'border-rose-300 bg-rose-600 text-white'}`}>
        {n.node}
      </span>
      <span className="absolute left-1/2 top-full mt-1.5 w-max -translate-x-1/2 rounded-full border border-purple-700/70 bg-[#120a20]/90 px-2 py-[1px] text-center backdrop-blur-sm">
        <span className="block text-[10px] leading-tight tracking-tighter">
          <span className="text-amber-300">{'★'.repeat(estrelas)}</span>
          <span className="text-purple-800">{'☆'.repeat(3 - estrelas)}</span>
        </span>
        <span className="block whitespace-nowrap text-[9px] font-bold text-purple-200/80">
          {locked ? 'bloqueada' : estrelas >= 3
            ? <span className="text-emerald-300">completo</span>
            : <><Gem className="mr-[2px] inline h-2.5 w-2.5 text-rose-300" />{custo}</>}
        </span>
      </span>
    </button>
  );
};

export const GbCityMap = ({ cidade, index, fases, atual, stars, recem, onPick, onClose }) => {
  const spots = SPOTS[index % SPOTS.length];
  // trilha pontilhada: o trecho só aparece depois de concluir a fase anterior
  const trechos = useMemo(() => {
    const out = [];
    for (let i = 1; i < spots.length; i++) {
      const p = spots[i - 1], q = spots[i], mx = (p.x + q.x) / 2;
      const alvo = Number(fases[i]?.node || 0);
      out.push({
        d: `M ${p.x} ${p.y} C ${mx} ${p.y}, ${mx} ${q.y}, ${q.x} ${q.y}`,
        aberto: alvo > 0 && atual > alvo,
        proximo: alvo > 0 && atual === alvo,
        novo: alvo > 0 && Number(recem || 0) + 1 === alvo,
      });
    }
    return out;
  }, [spots, fases, atual, recem]);
  const estadoDe = (n) => {
    const e = Number(stars[String(n.node)] || 0);
    if (n.node > atual) return 'locked';
    if (e >= 3) return 'ok';
    if (e > 0) return 'parcial';
    return 'atual';          // sem estrela ainda: bandeira vermelha
  };
  const totStars = fases.reduce((s, f) => s + Number(stars[String(f.node)] || 0), 0);
  const chefe = fases[0]?.opponent;
  const idxAtual = fases.findIndex((f) => Number(f.node) === atual);

  return (
    <div className="pwa-safe-top fixed inset-0 z-40 overflow-hidden bg-[#0b0712]" data-testid="gb-city-map">
      <div className="relative h-full w-full">
        <div className="pwa-safe-top absolute inset-x-0 top-0 z-20 flex items-center gap-3 bg-gradient-to-b from-[#0b0712] via-[#0b0712cc] to-transparent px-4 py-3">
          <img src={cidade.art} alt={cidade.nome} className="h-12 w-12 flex-none object-contain" />
          <div className="min-w-0 flex-1">
            <h3 className="flex items-center gap-1.5 truncate text-base font-black text-orange-300 sm:text-lg">
              {cidade.nome}
              {totStars >= 12 && <GbCrest className="h-5 w-5" />}
            </h3>
            <p className="text-[11px] text-purple-300/70">
              {cidade.lore} · fases {fases[0].node}–{fases[fases.length - 1].node} ·{' '}
              <span className="text-amber-300">★ {totStars}/12</span>
            </p>
          </div>
          {chefe && (
            <div className="hidden items-center gap-2 rounded-2xl border border-rose-800/50 bg-rose-950/25 px-2.5 py-1.5 sm:flex"
              data-testid="gb-city-boss">
              <img src={chefe.avatar} alt={chefe.nickname}
                className="h-10 w-10 rounded-full border-2 border-rose-400/80 object-cover shadow-[0_0_14px_rgba(255,84,112,.45)]" />
              <div className="leading-tight">
                <p className="text-[9px] font-black uppercase tracking-wide text-rose-300/60">Chefe da região</p>
                <p className="text-xs font-bold text-rose-100">{chefe.nickname}</p>
                {chefe.title && <p className="text-[10px] text-purple-300/60">{chefe.title}</p>}
              </div>
            </div>
          )}
          <button onClick={onClose} data-testid="gb-city-close"
            className="flex-none rounded-full border border-purple-700/60 bg-black/40 p-2 text-purple-200 backdrop-blur-sm">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="absolute inset-0">
          <GbFit testid="gb-city-fit">
            <img src={`/gb/world/m${index + 1}.webp`} alt={cidade.nome}
              className="absolute inset-0 h-full w-full object-cover" draggable="false" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_58%,rgba(9,5,18,.6)_100%)]" />
            <svg viewBox="0 0 100 100" preserveAspectRatio="none"
              className="pointer-events-none absolute inset-0 h-full w-full">
              <defs>
                {trechos.map((t, i) => (t.aberto && t.novo ? (
                  <mask key={i} id={`gbtr-${index}-${i}`} maskUnits="userSpaceOnUse">
                    <path d={t.d} fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="butt"
                      pathLength="100" strokeDasharray="100 100" strokeDashoffset="100">
                      <animate attributeName="stroke-dashoffset" from="100" to="0" dur="1.8s" fill="freeze" />
                    </path>
                  </mask>
                ) : null))}
              </defs>
              {trechos.map((t, i) => {
                if (!t.aberto && !t.proximo) return null;
                return (
                  <path key={i} d={t.d} fill="none" strokeLinecap="round"
                    mask={t.aberto && t.novo ? `url(#gbtr-${index}-${i})` : undefined}
                    strokeWidth={t.aberto ? 0.75 : 0.55}
                    strokeDasharray={t.aberto ? '0.25 2.6' : '0.25 3.4'}
                    stroke={t.aberto ? '#ffc25c' : 'rgba(244,238,252,.32)'}
                    style={t.aberto ? { filter: 'drop-shadow(0 0 2.5px rgba(255,176,32,.8))' } : undefined} />
                );
              })}
            </svg>
            {idxAtual >= 0 && (
              <GbTank from={idxAtual > 0 ? spots[idxAtual - 1] : null} to={spots[idxAtual]} size={44} />
            )}
            {fases.map((n, i) => (
              <Spot key={n.node} n={n} pos={spots[i]} estado={estadoDe(n)}
                estrelas={Number(stars[String(n.node)] || 0)} atualNode={atual} onPick={onPick} />
            ))}
          </GbFit>
        </div>

        <div className="absolute inset-x-0 bottom-0 z-20 flex flex-wrap items-center justify-center gap-3 bg-gradient-to-t from-[#0b0712] via-[#0b0712bb] to-transparent px-4 pb-2 pt-6 text-[10px] text-purple-200/80">
          <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm" style={{ background: FLAG.atual }} /> fase atual</span>
          <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm" style={{ background: FLAG.parcial }} /> 1 ou 2 estrelas</span>
          <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm" style={{ background: FLAG.ok }} /> 3 estrelas (completo)</span>
          <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> bloqueada</span>
          <span className="hidden sm:inline text-purple-300/50">· {MAP_LABEL[fases[0].map] || ''}</span>
        </div>
      </div>
    </div>
  );
};
